import {
  useNavigate,
  useParams,
  useSearchParams,
} from '@/hooks/useNavigate.ts';
import { useApplyJobDetails, useApplyJobSubmission } from '@/api/services.ts';
import { Input } from '@/components/Input.tsx';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Logo } from '@/assets';
import { Button } from '@/components/ui/button.tsx';
import { z } from 'zod';
import { ControllerFieldState, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { routes } from '@/constants.ts';
import { CSSProperties } from 'react';
import { usePageTitle } from '@/hooks/usePageTitle.ts';
import { cn } from '@/lib/utils.ts';
import { useTranslation } from 'react-i18next';
import { useAppLanguage } from '@/hooks/useAppLanguage.ts';
import { generatePath } from 'react-router-dom';
import { isInAppBrowser } from '@/pages/Answers/InAppBrowserBlock.tsx';

const LogoImage = ({ src }: { src: string }) => {
  return (
    <img
      className={cn(
        'w-auto object-contain',
        'h-[92px]',
        'portrait:[@media(min-height:711px)]:h-[120px]',
        'portrait:[@media(min-height:800px)]:h-[144px]',
        'md:h-[144px]'
      )}
      src={src}
      alt="logo"
    />
  );
};

const useInterviewSubmissionForm = () => {
  const { t } = useTranslation('applyJob');
  const schema = z.object({
    position_id: z.string().min(1, t('validation.position_required')),
    first_name: z.string().min(1, t('validation.first_name_required')),
    last_name: z.string().min(1, t('validation.last_name_required')),
    phone_number: z.string().min(1, t('validation.phone_number_required')),
  });
  return useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      position_id: '',
      first_name: '',
      last_name: '',
      phone_number: '',
    },
  });
};

const ApplyJob = () => {
  useAppLanguage();

  const navigate = useNavigate();
  const { t } = useTranslation('applyJob');
  const [searchParams] = useSearchParams();
  const params = useParams<{ property_id: string }>();
  const { data: jobDetails } = useApplyJobDetails(params.property_id || '');
  const { mutateAsync: triggerSubmissionApply, isLoading } =
    useApplyJobSubmission(params.property_id || '');
  const backgroundColor =
    searchParams.get('background_color') ||
    jobDetails?.background_color ||
    'inherit';
  const textColor =
    searchParams.get('text_color') || jobDetails?.text_color || '#ffffff';
  const hookForm = useInterviewSubmissionForm();

  const onSubmit = async (data: {
    position_id: string;
    first_name: string;
    last_name: string;
    phone_number: string;
  }) => {
    try {
      const response = await triggerSubmissionApply(data);
      let nextPath =
        routes.tutorial + '?invitation_key=' + response.invitation_key;
      const inApp = isInAppBrowser();
      if (inApp) {
        nextPath = generatePath(routes.applyFormFinish, {
          property_id: params.property_id || '',
        });
      }
      navigate(nextPath);
    } catch (error) {
      const errors = JSON.parse((error as Error).message);
      for (const field in errors) {
        if (field in data) {
          hookForm.setError(field as keyof typeof data, {
            type: 'custom',
            message: errors[field][0],
          });
        }
      }
    }
  };

  usePageTitle(jobDetails?.client_name, 'apply');

  if (!jobDetails) {
    return null;
  }

  const commonInputClasses = 'h-12 font-semibold uppercase';

  return (
    <div
      className="grid bg-[var(--bg-color)] place-items-center h-full min-h-[100dvh] min-h-[100vh] text-[var(--text-color)] px-5"
      style={
        {
          '--bg-color': backgroundColor,
          '--text-color': textColor,
          '--destructive': '0 90% 50%',
          '--text-shadow': '0px 4px 6px rgba(0, 0, 0, 0.45)',
        } as CSSProperties
      }
    >
      <div className="grid place-items-center max-w-md w-full h-full sm:h-auto sm:gap-5">
        <LogoImage src={jobDetails.logo_url} />
        <div className="grid gap-5">
          <div className="font-bold text-center text-xl mt-2 uppercase drop-shadow-[var(--text-shadow)]">
            {t('welcome_title')}
          </div>
          <div className="text-sm font-bold text-center drop-shadow-[var(--text-shadow)]">
            {t('welcome_message')}
          </div>
        </div>
        <Form {...hookForm}>
          <form
            className={'flex flex-col space-y-3 px-1 w-full mt-2'}
            onSubmit={hookForm.handleSubmit(onSubmit)}
          >
            <FormField
              control={hookForm.control}
              name="position_id"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel
                    className="drop-shadow-md"
                    errorClassName="text-error-message"
                  >
                    {t('position_label')}
                  </FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={isLoading || jobDetails.positions.length === 0}
                    >
                      <SelectTrigger
                        className={cn(
                          'w-full',
                          commonInputClasses,
                          getInputErrorClasses(fieldState)
                        )}
                      >
                        <SelectValue
                          placeholder={t('positions_available', {
                            count: jobDetails.positions.length,
                          })}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {jobDetails.positions.map((position) => (
                            <SelectItem
                              key={position.value}
                              value={String(position.value)}
                            >
                              {position.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage className="text-error-message error-blink" />
                </FormItem>
              )}
            />

            <FormField
              control={hookForm.control}
              name="first_name"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel
                    htmlFor="first_name"
                    errorClassName="text-error-message"
                  >
                    {t('first_name_label')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      id="first_name"
                      type="text"
                      className={cn(
                        commonInputClasses,
                        getInputErrorClasses(fieldState)
                      )}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="text-error-message error-blink" />
                </FormItem>
              )}
            />

            <FormField
              control={hookForm.control}
              name="last_name"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel
                    className="drop-shadow-[var(--text-shadow)]"
                    errorClassName="text-error-message"
                    htmlFor="last_name"
                  >
                    {t('last_name_label')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      id="last_name"
                      type="text"
                      className={cn(
                        commonInputClasses,
                        getInputErrorClasses(fieldState)
                      )}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="text-error-message error-blink" />
                </FormItem>
              )}
            />

            <FormField
              control={hookForm.control}
              name="phone_number"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel
                    className="drop-shadow-[var(--text-shadow)]"
                    errorClassName="text-error-message"
                    htmlFor="phone_number"
                  >
                    {t('phone_number_label')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      id="phone_number"
                      type="text"
                      kind="phone_number"
                      className={cn(
                        commonInputClasses,
                        getInputErrorClasses(fieldState)
                      )}
                      placeholder={t('phone_number_placeholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="text-error-message error-blink" />
                </FormItem>
              )}
            />

            <Button
              className="mt-8 mx-6"
              size="lg"
              disabled={isLoading}
              type="submit"
            >
              {t('start_interview_button')}
            </Button>
          </form>
        </Form>
        <div
          className="flex justify-end items-center font-bold text-xs mt-4 uppercase justify-self-end mb-2 text-white"
          style={
            {
              '--text-shadow': '0px 1px 2px rgba(0, 0, 0, 0.6)',
            } as CSSProperties
          }
        >
          <span className="drop-shadow-[var(--text-shadow)]">POWERED BY</span>{' '}
          <Logo className="size-8 ml-2 mr-1 drop-shadow-none" />{' '}
          <span className="text-base drop-shadow-[var(--text-shadow)]">
            123jobs.com <sup className="text-[8px] -top-2">TM</sup>
          </span>
        </div>
      </div>
    </div>
  );
};

function getInputErrorClasses(fieldState: ControllerFieldState) {
  if (fieldState.error) {
    return 'bg-red-100';
  }

  return '';
}

export default ApplyJob;
